import { useMemoizedFn } from 'ahooks';
import { debounce, find } from 'lodash';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Dialog, DialogPlugin, Form, Input, Loading, Select, Tag, Textarea } from 'tdesign-react';
import Text from '@/components/Text';
import Tip from '@/components/Tip';
import { createCluster, editCluster, getClusterDetail } from '@/services/resourceService';
import { UNSAFE_bugSetFieldsValue, isVoid, toTsOptions } from '@/utils/common';
import { CLUSTER_TYPES, clusterTypeOptions } from '@/utils/constant';
import formRulePattern from '@/utils/formRulePattern';
import { fetchVariableStencilList } from '@/slices/variableSlice';

const { FormItem } = Form;

function FormModal({
  visible,
  handleClose,
  id,
  envOptions,
  k8sOptions,
  stencilOptions,
  fetchVariableStencilList,
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  const type = Form.useWatch('type', form);
  const opsEnvId = Form.useWatch('opsEnvId', form);

  const isEdit = !isVoid(id); // 编辑

  const loadDetail = useMemoizedFn(async () => {
    if (isEdit) {
      setLoading(true);
      const { data } = await getClusterDetail(id);
      const { k8sId } = data;
      const params = { ...data, opsEnvId: data.envId };
      if (!isVoid(k8sId)) {
        Object.assign(params, {
          clusterInstallPath: '/root',
          contexts: find(k8sOptions, { value: k8sId })?.namespace,
        });
      }
      UNSAFE_bugSetFieldsValue(form, params);
      setLoading(false);
    } else {
      form.reset();
    }
  });

  useEffect(() => {
    if (visible) {
      loadDetail();
    }
  }, [loadDetail, visible]);

  useEffect(() => {
    if (isVoid(type)) return;
    fetchVariableStencilList({ scopeType: type });
  }, [type, fetchVariableStencilList]);

  const doSubmit = async (params) => {
    setSubmitting(true);
    try {
      if (isEdit) {
        await editCluster({
          ...params,
          id,
        });
      } else {
        await createCluster(params);
      }
      handleClose(true);
    } catch (error) {
      console.trace(error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      const { key, type } = fields;
      let _key = key;

      // K8s必须-asp后缀
      if (type === 1 && key.indexOf('-asp') < 0) {
        _key = `${key}-asp`;

        const dialog = DialogPlugin.confirm({
          header: '提示',
          theme: 'warning',
          body: (
            <>
              集群类型为K8S容器时，集群名称必须添加-asp后缀
              <br /> 自动将 <Tag>{key}</Tag> 替换为 <Tag>{_key}</Tag>
            </>
          ),
          confirmBtn: {
            content: '确定',
          },
          onClose: () => dialog.hide(),
          onConfirm: async () => {
            dialog.hide();
            doSubmit({ ...fields, key: _key });
            form.setFieldsValue({ key: _key });
          },
        });
      } else {
        doSubmit(fields);
      }
    }
  };

  // 稍微等待一下form字段调整
  const typeChange = debounce((value) => {
    // 虚拟机
    if (value === 0) {
      form.setFields([
        { name: 'clusterInstallPath', value: '/app/pulsar' },
        { name: 'gvarHostInstallUser', value: 'pulsar' },
      ]);
    }

    // K8s容器
    if (value === 1) {
      form.setFields([{ name: 'clusterInstallPath', value: '/root' }]);
    }
  }, 100);

  const k8sChange = (_, { selectedOptions }) => {
    const selected = selectedOptions[0];
    if (selected) {
      form.setFields([{ name: 'contexts', value: selected.namespace }]);
    } else {
      form.reset({ fields: ['contexts'] });
    }
  };

  return (
    <Dialog
      header={isEdit ? '修改集群' : '新增集群'}
      visible={visible}
      onClose={() => handleClose()}
      onConfirm={() => form.submit()}
      confirmBtn={{ loading: submitting, content: '确定' }}
      placement='center'
      destroyOnClose
      width={600}
    >
      <Loading loading={loading}>
        <Form form={form} onSubmit={handleSubmit} style={{ width: '90%' }} labelWidth={140}>
          <FormItem
            label={<Tip content='集群显示名称（可以用中文表示）'>集群显示名称</Tip>}
            name='name'
            rules={[{ required: true, message: '集群显示名称必填' }]}
          >
            <Input />
          </FormItem>
          <FormItem
            rules={[{ required: true, message: '集群名称必填' }, formRulePattern('name')]}
            name='key'
            className={isEdit ? 'hide' : null}
            label={
              <Tip
                content={
                  <>
                    集群名称（内部唯一标识符）
                    <br /> 集群类型为K8S容器时，必须添加-asp后缀
                  </>
                }
              >
                集群名称
              </Tip>
            }
          >
            <Input />
          </FormItem>
          <FormItem label='集群类型' name='type' rules={[{ required: true }]}>
            <Select options={toTsOptions(clusterTypeOptions)} onChange={typeChange} />
          </FormItem>
          <FormItem label='关联环境' name='opsEnvId' rules={[{ required: true }]}>
            <Select
              options={toTsOptions(envOptions)}
              onChange={() => {
                form.reset({ fields: ['k8sId'] });
              }}
            />
          </FormItem>
          {type === CLUSTER_TYPES.HOST && (
            <>
              <FormItem
                name='clusterInstallPath'
                label={
                  <Tip align='right' content='该集群的基础部署路径，自动部署时，会以该路径为前缀'>
                    部署基础路径
                  </Tip>
                }
              >
                <Input />
              </FormItem>
              <FormItem
                name='gvarHostInstallUser'
                label={
                  <Tip align='right' content='该集群的部署用户，如果操作系统没有该用户，会自动创建'>
                    部署用户
                  </Tip>
                }
              >
                <Input />
              </FormItem>
            </>
          )}
          {type === CLUSTER_TYPES.K8S && (
            <>
              <FormItem label='关联k8s集群' name='k8sId' rules={[{ required: true }]}>
                <Select
                  options={toTsOptions(k8sOptions.filter((item) => item.envId === opsEnvId))}
                  onChange={k8sChange}
                />
              </FormItem>
              <FormItem
                name='contexts'
                label={
                  <Tip align='right' content='Operator所在Namespace，从选择的k8s自动获取'>
                    Namespace
                  </Tip>
                }
              >
                <Text />
              </FormItem>
              <FormItem
                name='clusterInstallPath'
                label={
                  <Tip
                    align='right'
                    content='kubectl执行机器的基础目录，该集群的基础部署路径，自动部署时，会以该路径为前缀'
                  >
                    基础目录
                  </Tip>
                }
              >
                <Text />
              </FormItem>
              <FormItem
                name='namespace'
                rules={[{ required: true }, formRulePattern('name'), { max: 50 }]}
                label={
                  <Tip align='right' content='需要部署pulsar集群的Namespace'>
                    Namespace
                  </Tip>
                }
              >
                <Input />
              </FormItem>
            </>
          )}
          <FormItem name='varStencilId' label='变量模板' rules={[{ required: true }]}>
            <Select options={toTsOptions(stencilOptions)} />
          </FormItem>
          <FormItem name='desc' label='集群描述' rules={[{ required: true }]}>
            <Textarea placeholder='集群描述' />
          </FormItem>
        </Form>
      </Loading>
    </Dialog>
  );
}

export default connect(
  (state) => ({
    envOptions: state.resource.envOptions,
    k8sOptions: state.resource.k8sOptions,
    stencilOptions: state.variable.stencilList?.map((item) => ({
      label: item.stencilName,
      value: item.id,
    })),
  }),
  {
    fetchVariableStencilList,
  },
)(FormModal);
